import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

const currencyFormatter = new Intl.NumberFormat("el-GR", {
  style: "currency",
  currency: "EUR",
});

const formatValue = (value) => {
  if (value === undefined || value === null || value === "") return "-";
  return value;
};

const formatCurrency = (value) => {
  const amount = Number(value ?? 0);
  return Number.isFinite(amount) ? currencyFormatter.format(amount) : "-";
};

const splitValidationErrors = (validationErrors = []) =>
  validationErrors
    .flatMap((error) =>
      (typeof error === "string"
        ? error
        : (error?.message ?? error?.Message ?? "")
      )
        .split(/<br\s*\/?>|&lt;br\s*\/?&gt;|\r?\n/gi)
        .map((message) => message.trim().replace(/^-\s*/, "")),
    )
    .filter(Boolean);

const getExpenseRowSubject = (row) =>
  row?.appartmentName ??
  row?.childFullName ??
  row?.employeeFullName ??
  row?.childAmka ??
  row?.employeeAfm ??
  "-";

const SectionTitle = ({ title, count }) => (
  <Typography sx={{ fontSize: 14, fontWeight: 900 }}>
    {title}
    {Number.isFinite(count) ? ` (${count})` : ""}
  </Typography>
);

const EmptyTableRow = ({ colSpan }) => (
  <TableRow>
    <TableCell colSpan={colSpan} sx={{ color: "#667085", fontWeight: 600 }}>
      Δεν υπάρχουν εγγραφές.
    </TableCell>
  </TableRow>
);

const RejectedRowsTable = ({ rows = [] }) => (
  <Stack spacing={1}>
    <SectionTitle title="Γραμμές Δαπάνης" count={rows.length} />
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 900 }}>Δαπάνη</TableCell>
            <TableCell sx={{ fontWeight: 900 }}>Συσχέτιση</TableCell>
            <TableCell align="right" sx={{ fontWeight: 900 }}>
              Ποσό
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 900 }}>
              Εγκεκριμένο
            </TableCell>
            <TableCell sx={{ fontWeight: 900 }}>Κατάσταση</TableCell>
            <TableCell sx={{ fontWeight: 900 }}>Σχόλια</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 && <EmptyTableRow colSpan={6} />}
          {rows.map((row, index) => (
            <TableRow key={`${row?.id ?? "expense-row"}-${index}`}>
              <TableCell sx={{ minWidth: 220 }}>
                {formatValue(row?.expTypeName)}
              </TableCell>
              <TableCell sx={{ minWidth: 180 }}>
                {getExpenseRowSubject(row)}
              </TableCell>
              <TableCell align="right">
                {formatCurrency(row?.totalAmount)}
              </TableCell>
              <TableCell align="right">
                {formatCurrency(row?.approvedAmount)}
              </TableCell>
              <TableCell>{formatValue(row?.expStatusAbbr)}</TableCell>
              <TableCell sx={{ minWidth: 180, whiteSpace: "pre-wrap" }}>
                {formatValue(row?.expStatusComments)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  </Stack>
);

const RejectedPaymentsTable = ({ payments = [] }) => (
  <Stack spacing={1}>
    <SectionTitle title="Πληρωμές" count={payments.length} />
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 900 }}>Ημερομηνία</TableCell>
            <TableCell sx={{ fontWeight: 900 }}>Αριθμός</TableCell>
            <TableCell sx={{ fontWeight: 900 }}>Τύπος</TableCell>
            <TableCell align="right" sx={{ fontWeight: 900 }}>
              Ποσό
            </TableCell>
            <TableCell sx={{ fontWeight: 900 }}>Κατάσταση</TableCell>
            <TableCell sx={{ fontWeight: 900 }}>Σχόλια</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {payments.length === 0 && <EmptyTableRow colSpan={6} />}
          {payments.map((payment, index) => (
            <TableRow
              key={`${payment?.paymentTransHeaderId ?? "payment"}-${index}`}
            >
              <TableCell>{formatValue(payment?.paymentDateFormatted)}</TableCell>
              <TableCell>{formatValue(payment?.paymentNumber)}</TableCell>
              <TableCell sx={{ minWidth: 160 }}>
                {formatValue(payment?.transTypeName ?? payment?.transTypeAbbr)}
              </TableCell>
              <TableCell align="right">
                {formatCurrency(payment?.amount)}
              </TableCell>
              <TableCell>
                {formatValue(payment?.statusAbbr ?? payment?.statusName)}
              </TableCell>
              <TableCell sx={{ minWidth: 180, whiteSpace: "pre-wrap" }}>
                {formatValue(payment?.statusComments)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  </Stack>
);

const FinalReviewValidationDialog = ({ open, validation, onClose }) => {
  const validationErrors = splitValidationErrors(validation?.validationErrors);
  const rejectedRows = validation?.rejectedRows ?? [];
  const rejectedPayments = validation?.rejectedPayments ?? [];

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle variant="h4">Έλεγχος Παραστατικού</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {validationErrors.length > 0 && (
            <Alert severity="error" >
              <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 2.5 }}>
                {validationErrors.map((error, index) => (
                  <Box component="li" key={`${error}-${index}`}>
                    {error}
                  </Box>
                ))}
              </Stack>
            </Alert>
          )}
          <RejectedRowsTable rows={rejectedRows} />
          <RejectedPaymentsTable payments={rejectedPayments} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={onClose}>
          Κλείσιμο
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FinalReviewValidationDialog;
